import { faPencil } from '@fortawesome/pro-light-svg-icons'
import { Modify } from '@teinor/erp'
import { roundNumberAndDecimals } from '@teinor/erp/prices'
import { totalCalculateParams } from '@teinor/erp/prices/quote/calculate'
import { productTypesInversed } from '@teinor/erp/types/company/product'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useDebounce } from 'use-debounce'
import DisplayPrice from '../../../../../../../baseComponents/Displays/DisplayPrice'
import { CalculateParamsTy } from '../../../../../../../baseComponents/Displays/DisplayPrice/types'
import LoadingSpinner from '../../../../../../../baseComponents/LoaderDecider/LoadingSpinner'
import TwinIcon from '../../../../../../../baseComponents/TwinIcon'
import { ITIconToolTip } from '../../../../../../../baseComponents/TwinTable/InputTableLayout/InputTable/Components/Displays'
import { ITInputPercentage, ITInputPercentageProps, ITInputPercentageStateFull, ITInputPrice, ITInputPriceProps, ITInputPriceStateFull, ITInputQuantity, ITInputQuantityProps } from '../../../../../../../baseComponents/TwinTable/InputTableLayout/InputTable/Components/Inputs'
import { ITInputsOnChangeInputs } from '../../../../../../../baseComponents/TwinTable/InputTableLayout/InputTable/Components/Inputs/types'
import { RowData } from '../../../../../../../baseComponents/TwinTable/types'
import { CustomRenderString } from '../../../../../../../baseComponents/TwinTable/VirtualTableListing/CustomRenders'
import { RectangularInputProps, RectangularInputSmall } from '../../../../../../../forms/Input'
import { twinFetchPostJSON } from '../../../../../../../utils/globals/data'
import { TwinDictionary } from '../../../../../../../utils/globals/dictionary'
import useIsLoading from '../../../../../../../utils/hooks/useIsLoading'
import useOnClickOutside from '../../../../../../../utils/hooks/useOnClickOut'
import useTwinTranslation from '../../../../../../../utils/hooks/useTwinTranslation'
import { getISOCurrencyMain, getPreparePricesConfigParam } from '../../../../../../../utils/reducers/getters'
import { DisplayProductDot } from '../../../../../../inputTableLayouts/order/OrderLayout/OrderTable/TableComponents/Inputs/OrderSearchProductInput'
import { OnChangeOrderAddProduct } from '../../../../../../inputTableLayouts/order/OrderLayout/OrderTable/TableComponents/Inputs/OrderSearchProductInput/types'
import SearchBarProductResults from '../../../../../SearchBarProduct/SearchBarProductResults'
import { ProductExtendedTyShort } from '../../../../../types'
import { ProductEscandallingSearchProductFieldType, ProductEscandallingSearchProductInputChangeSearchFunc, ProductEscandallingTDTy } from '../types'

export const ProductEscandallingInputPrice: React.FC<ITInputPriceProps> = ({ value, ...all }) => {
    const readOnlyFnc = (allRowData: any) => !allRowData.ProductId
    return (
        <ITInputPrice value={value || '0'} readOnlyFnc={readOnlyFnc} {...all} />
    )
}

export const ProductEscandallingInputPercentage: React.FC<ITInputPercentageProps> = ({ value, ...all }) => {
    const readOnlyFnc = (allRowData: any) => !allRowData.ProductId
    return (
        <ITInputPercentage value={value || '0'} readOnlyFnc={readOnlyFnc}  {...all} />
    )
}

export const ProductEscandallingInputQuantity: React.FC<ITInputQuantityProps> = ({value,  ...all }) => {
    const readOnlyFnc = (allRowData: any) => !allRowData.ProductId
    return (
        <ITInputQuantity value={value} readOnlyFnc={readOnlyFnc} {...all} />
    )
}

export type ProductEscandallingSearchProductInputProps = Modify<RectangularInputProps, ProductEscandallingSearchProductInputLogicProps & {
    allRowData: RowData
    findFirstNameColumn?: boolean
    displayField?: string
}>

export const ProductEscandallingSearchProductInputName: React.FC<ProductEscandallingSearchProductInputProps> = ({ allRowData, field, onChange, findFirstNameColumn, displayField, selectedIds }) => {
    const { searchValue, changeSearchValue, listProducts, onSelectProduct, onFocus, ref, handleOnKeyDown, loading } = useProductEscandallingSearchProductInputLogic({ field, onChange, selectedIds })
    let myField = displayField ?? field
    if (allRowData.ProductId) {
        return <DisplayProductDot findFirstNameColumn={findFirstNameColumn} allRowData={allRowData}><div className='twin_elipsis_table_two_lines'>{allRowData[myField]}</div></DisplayProductDot>
    }
    if (loading) {
        return <div className='w-full'><LoadingSpinner className='w-20 h-20 mx-auto' /></div>
    }
    return (
        <div className='relative input_table_searchproductinput' ref={ref}>
            <RectangularInputSmall onChange={changeSearchValue} onKeyDown={handleOnKeyDown} value={searchValue} onFocus={onFocus} />
            <SearchBarProductResults products={listProducts} {...{ onSelectProduct, field }} />
        </div>
    )
}

interface ProductEscandallingSearchProductInputLogicProps {
    field: ProductEscandallingSearchProductFieldType
    onChange: OnChangeOrderAddProduct
    selectedIds: number[]
}

const useProductEscandallingSearchProductInputLogic = ({ field, onChange, selectedIds }: ProductEscandallingSearchProductInputLogicProps) => {
    const { startLoading, endLoading, loading } = useIsLoading()
    const [listProducts, setListProducts] = useState<ProductExtendedTyShort[] | null>(null)
    const [searchValue, setSearchValue] = useState<string>('')
    const [debounceValue] = useDebounce(searchValue, 200)
    const ref = useRef<any>(null)

    const changeSearchValue: ProductEscandallingSearchProductInputChangeSearchFunc = useCallback((myvalue) => {
        setSearchValue(myvalue)
    }, [setSearchValue])

    const onFocus = useCallback(() => {
        setListProducts([])
    }, [])

    const onBlur = useCallback(() => {
        setSearchValue('')
        setListProducts(null)
    }, [])

    const onSelectProduct: OnChangeOrderAddProduct = useCallback((product) => {
        startLoading()
        if(product){
            onChange(product)
            endLoading()
        }
    }, [onChange, startLoading, endLoading])

    const handleOnKeyDown = useCallback((event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
            if (listProducts && listProducts.length) {
                onSelectProduct(listProducts[0])
            }
        }
    }, [onSelectProduct, listProducts])

    useOnClickOutside(ref, onBlur)

    const getAllProducts = useCallback(async (mySearchValue: string) => {
        const extraWhere: TwinDictionary = {}
        const myiLike = {
            $iLike: '%' + mySearchValue + '%',
        }
        if (field === 'referenceAndName') {
            extraWhere['$or'] = [
                {
                    '$or': [
                        { name: myiLike },
                        { reference: myiLike },
                        { code_bar: myiLike },
                    ],
                },
                {
                    '$or': [
                        { name: mySearchValue },
                        { reference: mySearchValue },
                        { code_bar: mySearchValue },
                    ],
                },
            ]
        } else {
            extraWhere['$or'] = [
                {
                    [field]: myiLike,
                },
                {
                    [field]: mySearchValue,
                },
            ]
        }
        const result = await twinFetchPostJSON('/api/app/product/getAllProducts', {
            where: {
                 'productType': { 
                    '$or': [
                        productTypesInversed['material'], 
                        productTypesInversed['service']
                    ] 
                },
                'Product.id': {
                    '$not': [...selectedIds]
                },
                ...extraWhere
            },
            attributes: { include: ['ProductLangs.name', 'ProductImages.url', 'reference', 'code_bar']},
            limit: 15
        })
        setListProducts(result)
    }, [field, selectedIds])

    useEffect(() => {
        if (debounceValue !== '') {
            getAllProducts(debounceValue)
        } else if (debounceValue === '') {
            setListProducts((old) => {
                if (old) {
                    return []
                }
                return null
            })
        }
    }, [debounceValue, getAllProducts])

    return { searchValue, changeSearchValue, listProducts, onSelectProduct, onFocus, ref, handleOnKeyDown, loading }
}


export type ITDisplayEditFinalPriceProdEscandallingProps = ITDisplayEditFinalPriceProdEscandallingProdEscandallingLogicProps & {
    value: number
    CurrencyId: string
    readOnlyFnc?: (allRowData: RowData) => boolean
}

export const ITDisplayEditFinalPriceProdEscandalling: React.FC<ITDisplayEditFinalPriceProdEscandallingProps> = ({ allRowData, dataKey, onChange, readOnlyFnc, ...params }) => {
    const { t } = useTwinTranslation()
    const { editPrice, handleEditPrice, myOnBlur, ref } = useITDisplayEditFinalPriceProdEscandallingLogic({ onChange, allRowData, dataKey })
    const calculateParams = getPreparePricesConfigParam() as CalculateParamsTy
    const readOnly = !allRowData?.ProductId || readOnlyFnc?.(allRowData)
    return (
        <div className='flex items-center input_price w-full' ref={ref} >
            {editPrice ?
                <ITInputPriceStateFull className='edit_final_price' {...params} initialValue={params.value} dataKey={dataKey} allRowData={allRowData} onBlur={myOnBlur} value={String(params.value)} />
                :
                <DisplayPrice calculateParams={{ ...calculateParams, 'roundingPriceType': 'medium' }} className={'input_table_displaynumber_r '} {...params} with_vat={false} />
            }
            {!readOnly ? <ITIconToolTip className='cursor-pointer hover:text-green-43' icon={faPencil} id={'editFinalPrice_' + allRowData.id} message={t('editTotalPrice', 'Editar precio total')} onClick={handleEditPrice} /> :
                null}
        </div>
    )
}

interface ITDisplayEditFinalPriceProdEscandallingProdEscandallingLogicProps {
    allRowData: RowData
    onChange: ITInputsOnChangeInputs
    dataKey: string
}
export type EditFinalPriceValueTy = {
    keyValue: string
    value: string
}
const useITDisplayEditFinalPriceProdEscandallingLogic = ({ onChange, allRowData, dataKey }: ITDisplayEditFinalPriceProdEscandallingProdEscandallingLogicProps) => {
    const [editPrice, setEditPrice] = useState<boolean>(false)
    const ref = useRef<any>(null)

    const handleEditPrice = useCallback(() => {
        setEditPrice((old) => !old)
        setTimeout(() => {
            const input = ref.current?.querySelector('.edit_final_price .input_field_input')
            if (input) {
                input.focus()
            }
        }, 40)
    }, [setEditPrice])

    const myOnBlur = useCallback((myValue: string) => {
        if (myValue) {
            onChange(allRowData.absoluteIndex || allRowData.rowIndex, dataKey, myValue)
        }
        handleEditPrice()
    }, [onChange, handleEditPrice, allRowData, dataKey])
    return { handleEditPrice, editPrice, myOnBlur, ref }
}


interface DisplayEditCostTotalProductEscandallingProps extends DisplayEditCostTotalProductEscandallingLogicProps {
    totalCostMargin: number
}

export const DisplayEditCostTotalProductEscandalling: React.FC<DisplayEditCostTotalProductEscandallingProps> = ({marginCalculated, totalCost, totalCostMargin, setData}) => {
    const { editPrice, handleChangeCostProductMargin, ref, handleEditPrice } = useDisplayEditCostTotalProductEscandallingLogic({ marginCalculated, totalCost, setData })
    const CurrencyId = getISOCurrencyMain()
    return (
        <div className='flex items-center medium14' ref={ref}>
            {editPrice ?
                <ITInputPriceStateFull className='input_edit_cost_escandalling_price' CurrencyId={CurrencyId}onBlur={(val) => handleChangeCostProductMargin(val)} initialValue={totalCostMargin} dataKey='' allRowData={{}}/>
                :
                <DisplayPrice value={totalCostMargin} CurrencyId={CurrencyId} with_vat={false} calculateParams={{
                    decimals: 2,
                    rounding: '0.01',
                    roundingPriceType: 'medium'
                }} />
            }
            <TwinIcon icon={faPencil} className='ml-5 hover:text-green-43 cursor-pointer' id={'editCostProduct'} onClick={handleEditPrice}/>
        </div>
    )
}

interface DisplayEditCostTotalProductEscandallingLogicProps {
    totalCost: number
    marginCalculated: number
    setData: React.Dispatch<React.SetStateAction<ProductEscandallingTDTy>>
}

const useDisplayEditCostTotalProductEscandallingLogic = ({ totalCost, marginCalculated, setData }: DisplayEditCostTotalProductEscandallingLogicProps) => {
    const [editPrice, setEditPrice] = useState<boolean>(false)
    const ref = useRef<any>(null)

    const handleEditPrice = useCallback(() => {
        setEditPrice((old) => !old)
        setTimeout(() => {
            const input = ref.current?.querySelector('.input_edit_cost_escandalling_price input')
            if (input) {
                input.focus()
            }
        }, 40)
    }, [setEditPrice])

    const handleChangeCostProductMargin = useCallback((myVal: string) => {
        const parsedMyVal = roundNumberAndDecimals(parseFloat(String(myVal)), totalCalculateParams)
        const parsedTotalCost = parseFloat(String(totalCost))
        const parsedMarginCalculated = parseFloat(String(marginCalculated))
        if (parsedTotalCost !== parsedMyVal){
            const newCalculatedMargin = roundNumberAndDecimals(((parsedMyVal * 100) / parsedTotalCost) - 100, totalCalculateParams)
            setData((old)=>{
                const copyOld = JSON.parse(JSON.stringify(old))
                if(copyOld.tableData){
                    let totalAccumulated = 0
                    for (const key in copyOld.tableData) {
                        const element = copyOld.tableData[key]
                        if(element.id !== 'addButton'){
                            if(element.cost !== null){
                                const parsedCost = parseFloat(String(element.cost))
                               element.margin =  roundNumberAndDecimals((newCalculatedMargin * element.margin) / parsedMarginCalculated, totalCalculateParams)
                                element.unitPriceCostWithMargin = roundNumberAndDecimals(parsedCost + (element.margin / 100 * parsedCost), totalCalculateParams)
                                const parsedRowPriceCost = parseFloat(String(element.rowPriceCost))
                                element.rowPriceCostWithMargin = roundNumberAndDecimals(parsedRowPriceCost + (element.margin / 100 * parsedRowPriceCost), totalCalculateParams)
                                totalAccumulated = roundNumberAndDecimals(element.rowPriceCostWithMargin + totalAccumulated, totalCalculateParams)
                            }
                            const isLast = (copyOld.tableData.length - 2) === parseInt(key)
                            if (isLast) {
                                const myDiff = roundNumberAndDecimals(parsedMyVal - totalAccumulated, totalCalculateParams)
                                if (myDiff) {
                                    element.rowPriceCostWithMargin = roundNumberAndDecimals(element.rowPriceCostWithMargin + myDiff, totalCalculateParams)
                                }
                            }
                        }
                    }
                }
                return copyOld
            })
        }
        handleEditPrice()
    }, [setData, totalCost, marginCalculated, handleEditPrice])

    return { editPrice, setEditPrice, handleChangeCostProductMargin, ref, handleEditPrice }
}

interface DisplayEditPercentageTotalProductEscandallingProps extends DisplayEditCostTotalProductEscandallingLogicProps {}

export const DisplayEditPercentageTotalProductEscandalling: React.FC<DisplayEditPercentageTotalProductEscandallingProps> = ({ marginCalculated, totalCost, setData }) => {
    const { editPrice, handleChangeCostProductMargin, ref, handleEditPrice } = useDisplayEditPercentageTotalProductEscandallingLogic({ marginCalculated, totalCost, setData })
    return (
        <div className='flex items-center medium14' ref={ref}>
            {editPrice ?
                <ITInputPercentageStateFull onBlur={handleChangeCostProductMargin} initialValue={marginCalculated} dataKey={''} allRowData={{}} />
                :
                <CustomRenderString className='medium14' value={marginCalculated + ' %'} />
            }
            <TwinIcon icon={faPencil} className='ml-5 hover:text-green-43 cursor-pointer' id={'editCostProduct'} onClick={handleEditPrice} />
        </div>
    )
}

interface DisplayEditPercentageTotalProductEscandallingLogicProps {
    totalCost: number
    marginCalculated: number
    setData: React.Dispatch<React.SetStateAction<ProductEscandallingTDTy>>
}

const useDisplayEditPercentageTotalProductEscandallingLogic = ({ totalCost, marginCalculated, setData }: DisplayEditPercentageTotalProductEscandallingLogicProps) => {
    const [editPrice, setEditPrice] = useState<boolean>(false)
    const ref = useRef<any>(null)

    const handleEditPrice = useCallback(() => {
        setEditPrice((old) => !old)
        setTimeout(() => {
            const input = ref.current?.querySelector('.input_edit_cost_escandalling_price input')
            if (input) {
                input.focus()
            }
        }, 40)
    }, [setEditPrice])

    const handleChangeCostProductMargin = useCallback((value: string) => {
        if (value) {
            const parsedMyVal = parseFloat(String(value))
            const parsedMarginCalculated = parseFloat(String(marginCalculated))
            if (parsedMarginCalculated !== parsedMyVal) {
                setData((old) => {
                    const copyOld = JSON.parse(JSON.stringify(old))
                    if (copyOld.tableData) {
                        for (const element of copyOld.tableData) {
                            if (element.cost) {
                                const parsedCost = parseFloat(String(element.cost))
                                element.margin = roundNumberAndDecimals(parsedMyVal, totalCalculateParams)
                                element.unitPriceCostWithMargin = roundNumberAndDecimals(parsedCost + (element.margin / 100 * parsedCost), totalCalculateParams)
                                const parsedRowPriceCost = parseFloat(String(element.rowPriceCost))
                                element.rowPriceCostWithMargin = roundNumberAndDecimals(parsedRowPriceCost + (element.margin / 100 * parsedRowPriceCost), totalCalculateParams)
                            }
                        }
                    }
                    return copyOld
                })
            }
        }
        handleEditPrice()
    }, [marginCalculated, handleEditPrice, setData])

    return { editPrice, setEditPrice, handleChangeCostProductMargin, ref, handleEditPrice }
}